import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  reportFoundItem, getEligibleBookingsForDamageClaim,
  LOCATION_IN_VEHICLE_OPTIONS,
} from '../../services/disputeService';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import PhotoUploader from '../../components/PhotoUploader';
import { ACCEPTED_IMAGE_TYPES } from '../../services/storageService';
import toast from 'react-hot-toast';

// Driver-eligible trips for found-item reporting: completed within last 14 days (same window as rider's report)
async function getDriverEligibleTrips(driverId) {
  const cutoff = new Date(Date.now() - 14 * 86400000);
  const q = query(
    collection(db, 'bookings'),
    where('driverId', '==', driverId),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b => {
      const c = b.completedAt?.toDate?.() || (b.completedAt ? new Date(b.completedAt) : null);
      return c && c >= cutoff;
    });
}

export default function ReportFoundItem() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  const prefilledBookingId = location.state?.bookingId || null;

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bookingId: prefilledBookingId || '',
    description: '',
    locationInVehicle: '',
    notes: '',
    photos: [],
  });

  useEffect(() => {
    if (!userProfile?.id) return;
    getDriverEligibleTrips(userProfile.id)
      .then(setBookings)
      .catch((err) => toast.error(err.message || 'Could not load trips'))
      .finally(() => setBookingsLoading(false));
  }, [userProfile?.id]);

  const handleSubmit = async () => {
    if (!form.bookingId) { toast.error('Select a trip'); return; }
    if (!form.description || form.description.trim().length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const result = await reportFoundItem({
        bookingId: form.bookingId,
        description: form.description.trim(),
        locationInVehicle: form.locationInVehicle || null,
        notes: form.notes || null,
        stagedPhotoUrls: form.photos.map(p => p.url),
      });

      if (result.alreadyExists) {
        toast(`A case already exists for this trip (${result.referenceNumber}). Opening it now.`, { icon: 'ℹ️', duration: 5000 });
        navigate(`/driver/lost-items/${result.caseId}`);
        return;
      }

      toast.success(`Reported ${result.referenceNumber}. Rider has been notified.`);
      navigate(`/driver/lost-items/${result.caseId}`);
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  const selectedBooking = bookings.find(b => b.id === form.bookingId);

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Report a found item</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Found something in your vehicle from a recent trip? Report it here. The rider will be notified
        and you can arrange return or pickup.
      </p>

      {/* Trip selector */}
      <div className="form-group">
        <label className="form-label">From which trip? *</label>
        {bookingsLoading ? (
          <div className="p-3 text-muted text-[0.9rem]">Loading recent trips...</div>
        ) : bookings.length === 0 ? (
          <div className="p-3.5 rounded-[10px] text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
            No eligible trips. You can only report found items from completed trips within the last 14 days.
          </div>
        ) : (
          <select className="form-input form-select" value={form.bookingId}
            onChange={(e) => setForm({ ...form, bookingId: e.target.value })}>
            <option value="">Select a trip...</option>
            {bookings.map(b => {
              const completedAt = b.completedAt?.toDate?.() || new Date(b.completedAt);
              return (
                <option key={b.id} value={b.id}>
                  {b.referenceNumber || b.id.substring(0, 8)} — {completedAt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} — {b.riderName || 'Rider'}
                </option>
              );
            })}
          </select>
        )}
        {selectedBooking && (
          <div className="p-2.5 rounded-lg bg-slate-50 mt-1.5 text-[0.82rem] text-body">
            Rider: <strong>{selectedBooking.riderName || 'Rider'}</strong> ·
            Route: {selectedBooking.pickupAddress?.split(',')[0]} → {selectedBooking.destinationAddress?.split(',')[0]}
          </div>
        )}
      </div>

      {/* Description */}
      <div className="form-group">
        <label className="form-label">Describe the item * (min 10 chars)</label>
        <textarea className="form-input" rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="e.g. Black wallet with credit cards. Found on back seat after trip."
        />
        <div className="text-[0.75rem] text-muted mt-1">
          {form.description.length} characters
        </div>
      </div>

      {/* Location in vehicle (optional but helpful) */}
      <div className="form-group">
        <label className="form-label">Where in the vehicle did you find it? (optional)</label>
        <select className="form-input form-select" value={form.locationInVehicle}
          onChange={(e) => setForm({ ...form, locationInVehicle: e.target.value })}>
          <option value="">Not sure / other</option>
          {LOCATION_IN_VEHICLE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Additional notes */}
      <div className="form-group">
        <label className="form-label">Additional notes (optional)</label>
        <textarea className="form-input" rows={2}
          placeholder="Any other details..."
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>

      {/* Photos */}
      <PhotoUploader
        userId={userProfile?.id}
        value={form.photos}
        onChange={(photos) => setForm({ ...form, photos })}
        maxFiles={3}
        acceptedTypes={ACCEPTED_IMAGE_TYPES}
        label="Photos of the item (optional)"
        helperText="Adding photos helps the rider identify the item. Up to 3 images."
      />

      {/* What happens next */}
      <div className="p-3.5 rounded-xl mb-4 text-[0.85rem] leading-relaxed" style={{ background: '#f0fdfa', border: '1px solid #99f6e4', color: '#0f766e' }}>
        <strong>What happens next:</strong>
        <ul className="mt-1.5 mb-0 pl-5">
          <li>The rider gets notified that you have an item from their trip.</li>
          <li>If they want it back, they'll offer you a return fee (minimum $30).</li>
          <li>You can accept the fee and deliver, or decline and arrange pickup.</li>
          <li>You're responsible for the item for 14 days. After that, you may dispose of it.</li>
        </ul>
      </div>

      <button onClick={handleSubmit} disabled={submitting || bookings.length === 0} className="w-full p-3.5 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[1rem]"
        style={{ opacity: (submitting || bookings.length === 0) ? 0.6 : 1 }}>
        {submitting ? 'Submitting...' : 'Report found item'}
      </button>
      <BackButtonBottom label="Back" />
    </div>
  );
}